// models
import { DatasetFiltersModel } from 'models/dataset-filters.model';
import { VariantWithLabel } from 'models/variant.model';

export const showWarningIconInEditor = (data: VariantWithLabel[]) => {
    const groupedByVariantType: {
        [key: string]: { [key: string]: { edits: any[]; labels: any[] } };
    } = {};

    // Group data by variantType and originalVariantId
    data.forEach((item) => {
        const variantType = item.variant_type;
        const originalVariantId = item.component_id;

        if (!groupedByVariantType[variantType]) {
            groupedByVariantType[variantType] = {};
        }
        if (!groupedByVariantType[variantType][originalVariantId]) {
            groupedByVariantType[variantType][originalVariantId] = {
                edits: [],
                labels: [],
            };
        }

        groupedByVariantType[variantType][originalVariantId].edits.push(
            item.content,
        );

        if (item.label) {
            groupedByVariantType[variantType][originalVariantId].labels.push(
                item.label?.id,
            );
        }
    });

    // Determine whether to show warning icons
    const finalResult: { [key: string]: boolean }[] = [];
    Object.keys(groupedByVariantType).forEach((variantType) => {
        const entries = Object.values(groupedByVariantType[variantType]);

        if (entries.length > 1) {
            const hasInconsistentLength = entries.some(
                (item) => item.edits.length !== item.labels.length,
            );

            if (hasInconsistentLength) {
                finalResult.push({ [variantType]: true });
            } else {
                const allLabels = entries.flatMap((item) =>
                    item.labels.map((label) => label),
                );
                const uniqueLabels = new Set(allLabels);

                const allHaveLabels = Array.from(uniqueLabels).every(
                    (uniqueLabel) =>
                        entries.every((entry) =>
                            entry.labels.some((label) => label === uniqueLabel),
                        ),
                );

                if (!allHaveLabels) {
                    finalResult.push({ [variantType]: true });
                } else {
                    finalResult.push({ [variantType]: false });
                }
            }
        } else {
            finalResult.push({ [variantType]: false });
        }
    });

    const sortedData = finalResult.sort((a, b) => {
        const keyA = Object.keys(a)[0];
        const keyB = Object.keys(b)[0];
        return keyA.localeCompare(keyB);
    });

    return sortedData;
};

export function normalizeEmptyDatasetFilters(
    data: DatasetFiltersModel,
): DatasetFiltersModel {
    if (data?.filters) {
        const filters = data.filters;
        const allEmpty = Object.values(filters).every(
            (val) => Array.isArray(val) && val.length === 0,
        );

        if (allEmpty) {
            data.filters = {};
        }
    }
    return data;
}

export const getDuplicateComponentMutualLabelMap = (
    variants: VariantWithLabel[],
): Record<string, number[]> => {
    const typeToVariants: Record<string, VariantWithLabel[]> = {};

    variants.forEach((v) => {
        const typeId = v.component_type_id;
        if (!typeToVariants[typeId]) {
            typeToVariants[typeId] = [];
        }
        typeToVariants[typeId].push(v);
    });

    const duplicateMap: Record<string, number[]> = {};

    Object.entries(typeToVariants).forEach(([typeId, comps]) => {
        const uniqueCompIds = new Set(comps.map((c) => c.component_id));
        if (uniqueCompIds.size > 1) {
            // Step 1: Group variants by component_id
            const compIdToLabels: Record<string, Set<number>> = {};
            comps.forEach((v) => {
                if (!compIdToLabels[v.component_id]) {
                    compIdToLabels[v.component_id] = new Set();
                }
                if (v.label?.id != null) {
                    compIdToLabels[v.component_id].add(v.label.id);
                }
            });

            // Step 2: Intersect labels across all components
            const allLabelSets = Object.values(compIdToLabels);
            const mutualLabels = allLabelSets.reduce((acc, set) => {
                if (!acc) return new Set(set);
                return new Set([...acc].filter((x) => set.has(x)));
            }, null as Set<number> | null);

            duplicateMap[typeId] = mutualLabels ? Array.from(mutualLabels) : [];
        }
    });

    return duplicateMap;
};
